import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Transform, Type, plainToInstance } from "class-transformer";
import { isArray } from "lodash-es";
import { FormAnswer, FormAnswerObject } from "./form-answer.model";
import { FormTemplateQuestion } from "./form-template-question.model";
import { SectionDirection } from "./form-template-section.model";
import { QuestionEnumValue } from "./question-list.model";

export type QuestionRankFormControlOption = {
  value: string | number;
  text?: string;
  isMultiple?: boolean;
};
type QuestionRankFormControlValue = QuestionRankFormControlOption | QuestionRankFormControlOption[];

export class QuestionRank extends FormTemplateQuestion {
  @Transform(({ obj }) => ({
    name: obj.questionEnum.name,
    description: obj.questionEnum.description,
    enumValues: obj.questionEnum.enumValues.map((q) => plainToInstance(QuestionEnumValue, q)),
    isMultiple: !!obj.questionEnum.isMultiple,
  }))
  questionEnum: {
    name: string;
    description: string;
    enumValues: QuestionEnumValue[];
    isMultiple: boolean;
  };

  public collectAnswer(formGroup: FormGroup, isEdit: boolean): FormAnswer[] | null {
    const questionAnswers = formGroup.value[this.getName()];

    //if (["", null].includes(questionAnswers)) {
    // Check if questionAnswers is null or undefined
    if (questionAnswers === null || questionAnswers === undefined) {
      return null;
    }
    // new condtion to handle list question type for comments
    //if questionAnswers is an array, we need to handle multiple answers else single answer
    if (typeof questionAnswers === "object" && questionAnswers !== null) {
      const formAnswers: FormAnswer[] = [];
      for (let i = 0; i < questionAnswers.length; i++) {
        const formAnswer = new FormAnswer();
        formAnswer.formTemplateQuestionId = this.id;
        formAnswer.isMultiple = questionAnswers[i].isMultiple;
        formAnswer[this.getAnswerDataField(isEdit)[0]] = Number(questionAnswers[i].value);

        //pass null if text is empty otherwise data not updated in DB
        formAnswer[this.getAnswerDataField(isEdit)[1]] = !!questionAnswers[i].text
          ? encodeURIComponent(questionAnswers[i].text)
          : null;

        formAnswers.push(formAnswer);
      }
      return formAnswers;
    } else {
      const formAnswer = new FormAnswer();
      formAnswer.formTemplateQuestionId = this.id;
      formAnswer[this.getAnswerDataField(isEdit)[1]] = encodeURIComponent(
        formGroup.value[this.getName()]
      );
      return [formAnswer];
    }
  }

  // Override parent method
  public collectFormControl(answersToDefaultTo: FormAnswerObject): Record<string, FormControl> {
    let defaultValue: QuestionRankFormControlValue =
      this.defaultValue !== null ? { value: this.defaultValue, text: "" } : this.defaultValue;

    if (answersToDefaultTo[this.id] !== undefined) {
      let hasEditedAnswer = true;

      if (isArray(answersToDefaultTo[this.id])) {
        // Sort by id ascending so each row represents a stable rank slot:
        // slot 1 = lowest-id row, slot 2 = next, etc. This is what `submitFormEdit`
        // relies on to persist user reordering — see response-verification.service.ts.
        const sortedAnswers = [...(answersToDefaultTo[this.id] as FormAnswer[])].sort(
          (a, b) => (a.id ?? 0) - (b.id ?? 0)
        );
        // Check each answer for edited status and set defaultValue accordingly
        defaultValue = sortedAnswers.map((a) => {
          // Determine if this answer is edited
          let edited = true;
          const updatedAt = a["updatedAt"];
          const createdAt = a["createdAt"];
          const originalValue = a[this.getAnswerDataField(false)[0]];
          const localDate = new Date(createdAt);
          if (
            localDate.toString() === updatedAt.toString() &&
            originalValue != null &&
            originalValue !== ""
          ) {
            edited = false;
          }
          return {
            value: a[this.getAnswerDataField(edited)[0]],
            text: a[this.getAnswerDataField(edited)[1]] || "",
            isMultiple: true,
          };
        });
      } else {
        const answerValue = answersToDefaultTo[this.id][this.getAnswerDataField(false)[0]];
        const originalAnswer = answerValue != null ? answerValue.toString() : "";
        const updatedAt = answersToDefaultTo[this.id]["updatedAt"];
        const utcDate = answersToDefaultTo[this.id]["createdAt"];
        const localDate = new Date(utcDate);

        // Check if the answer has been edited
        // If the updatedAt is same as createdAt, it means the answer has not been edited
        // If the original answer is not null or empty, we use it as default value
        if (
          localDate.toString() == updatedAt.toString() &&
          originalAnswer != null &&
          originalAnswer != ""
        ) {
          hasEditedAnswer = false;
        }
        defaultValue = {
          value: answersToDefaultTo[this.id][this.getAnswerDataField(hasEditedAnswer)[0]],
          text: answersToDefaultTo[this.id][this.getAnswerDataField(hasEditedAnswer)[1]] || "",
        };
      }
    }
    return {
      [this.getName()]: new FormControl<QuestionRankFormControlValue>(
        defaultValue,
        this.required ? Validators.required : Validators.nullValidator
      ),
    };
  }

  public readDataFromAnswers(
    answers: FormAnswerObject,
    isEdit = false,
    field: "label" | "value" = "label"
  ): string | number {
    if (answers[this.id]) {
      if (isArray(answers[this.id])) {
        const texts: string[] = [];
        const multiAnswers = answers[this.id] as FormAnswer[];

        for (let i = 0; i < multiAnswers.length; i++) {
          const answer = multiAnswers[i];
          const enumValue = this.questionEnum.enumValues.find(
            (enumValue) => enumValue.value === answer[this.getAnswerDataField(isEdit)[0]]
          );
          if (enumValue) {
            const label = enumValue[field].toString();
            texts.push(`${texts.length + 1}. ${label}`);
          }
        }
        return texts.join(", ");
      }
    }
    return "";
  }
  public getContentDirection(): string {
    if (this.viewMeta?.contentDirection) {
      return this.viewMeta.contentDirection;
    }
    return SectionDirection.Vertical;
  }
}
